// GENERATED by kanban-board-be `npm run contract:export`. Do not edit.
// Source of truth: kanban-board-be/src/contract. Change it there and re-export.

import { z } from "zod";

/**
 * The sync protocol.
 *
 * ------------------------------------------------------------------------
 * PROTOCOL_VERSION
 *
 * Bump this on ANY breaking change to the messages below: a renamed field, a
 * changed meaning, a new required property. Do not bump it for additive,
 * ignorable fields.
 *
 * It exists because the board ships as two repositories, deployed separately.
 * A browser that has had a tab open since before a deploy is running the old
 * client against the new server, and the failure mode without this check is
 * not a visible error — it is a client that misreads a delta, writes the
 * misreading into its IndexedDB replica, and then serves that corruption to
 * the person using it until they clear site data. A refused connection and a
 * reload prompt is enormously preferable.
 *
 * In a monorepo the two sides could not disagree. This integer is what buys
 * the same guarantee across two.
 * ------------------------------------------------------------------------
 */
export const PROTOCOL_VERSION = 1;

/** Tables a client holds a replica of. Mirrors SYNCED_TABLES in the schema. */
export const SyncedTableNameSchema = z.enum([
  "users",
  "projects",
  "projectMembers",
  "workflowStates",
  "labels",
  "tasks",
  "taskLabels",
  "comments",
  "activity",
  "taskSubscriptions",
  "notifications",
  "notificationPreferences",
]);
export type SyncedTableName = z.infer<typeof SyncedTableNameSchema>;

// ---------------------------------------------------------------------------
// Client to server
// ---------------------------------------------------------------------------

/**
 * First message on every connection.
 *
 * `lastVersion` is the entire state of the client as far as the server is
 * concerned: the highest version it has durably stored. 0 means "I have
 * nothing, send me everything". There is no session resumption to get wrong
 * and no server-side per-client state to leak — the client tells us where it
 * is, every time it connects.
 */
export const HelloSchema = z.object({
  type: z.literal("hello"),
  protocolVersion: z.number().int(),
  lastVersion: z.number().int().min(0),
});

/**
 * A change the user made, already applied in their browser.
 *
 * `mutationId` is minted by the client and is the idempotency key. A dropped
 * connection mid-mutation is normal — the client reconnects and resends
 * anything unacknowledged, and the server must apply each exactly once. The
 * mutation queue retries forever; the id is what makes that safe.
 */
export const MutateSchema = z.object({
  type: z.literal("mutate"),
  mutationId: z.uuid(),
  name: z.string().min(1),
  args: z.unknown(),
});

export const PingSchema = z.object({ type: z.literal("ping") });

export const ClientMessageSchema = z.discriminatedUnion("type", [
  HelloSchema,
  MutateSchema,
  PingSchema,
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

// ---------------------------------------------------------------------------
// Server to client
// ---------------------------------------------------------------------------

export const HelloOkSchema = z.object({
  type: z.literal("hello_ok"),
  protocolVersion: z.number().int(),
  userId: z.uuid(),
  workspaceId: z.uuid(),
  /** Where the workspace is now. The client is caught up once it reaches this. */
  currentVersion: z.number().int(),
  /** True when the server is about to send the whole dataset, not a delta. */
  bootstrap: z.boolean(),
});

export const ProtocolMismatchSchema = z.object({
  type: z.literal("protocol_mismatch"),
  serverProtocolVersion: z.number().int(),
  action: z.literal("reload"),
});

/**
 * A batch of changed rows.
 *
 * Rows are whole, not patches. A patch would be smaller on the wire and would
 * require the client and server to agree about how to merge it, which is
 * precisely the kind of shared logic that drifts between two repositories.
 * Sending the row as it now stands means a client can only ever be exactly
 * right or completely behind, never subtly wrong.
 *
 * A soft-deleted row arrives here like any other change, with `deletedAt` set.
 * That is how a delete reaches a client that was offline when it happened.
 */
export const DeltaSchema = z.object({
  type: z.literal("delta"),
  /** Apply the whole batch, then store this as lastVersion. Never mid-batch. */
  upTo: z.number().int(),
  changes: z.array(
    z.object({
      table: SyncedTableNameSchema,
      rows: z.array(z.record(z.string(), z.unknown())),
    }),
  ),
  /** More batches follow. The client stays in its catching-up state until false. */
  hasMore: z.boolean(),
});

export const MutationAckSchema = z.object({
  type: z.literal("mutation_ack"),
  mutationId: z.uuid(),
  version: z.number().int(),
});

/**
 * The mutation will not be applied, ever. The client rolls back its optimistic
 * change and drops it from the queue — as distinct from a transport failure,
 * where the client keeps the mutation and retries on reconnect.
 */
export const MutationErrorSchema = z.object({
  type: z.literal("mutation_error"),
  mutationId: z.uuid(),
  code: z.string(),
  message: z.string(),
});

export const PongSchema = z.object({ type: z.literal("pong") });

/**
 * Throw the replica away and bootstrap again from version 0.
 *
 * Sent when what this user is allowed to see changes — most often, being added
 * to a private project. A delta cannot express that: the project's existing
 * cards were written long before the client's high-water mark, so "everything
 * after version N" will never include them. The general fix is a targeted
 * backfill per newly-visible scope, which is a lot of machinery for an event
 * that happens a few times a year. Re-bootstrapping a few megabytes is
 * instant, always correct, and has no edge cases.
 */
export const ResyncSchema = z.object({
  type: z.literal("resync"),
  reason: z.string(),
});

export const ServerMessageSchema = z.discriminatedUnion("type", [
  HelloOkSchema,
  ProtocolMismatchSchema,
  DeltaSchema,
  MutationAckSchema,
  MutationErrorSchema,
  PongSchema,
  ResyncSchema,
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

/** Rows per delta batch. Keeps a bootstrap from arriving as one enormous frame. */
export const DELTA_BATCH_SIZE = 500;
